import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
  LayoutDashboard, UserCog, Users, Coins, Crown, HelpCircle,
  LogOut, Menu, X, ChevronLeft, ChevronRight, ShieldCheck, HandCoins, Store, Home,
  Tv, Radio,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { NotificationBell } from '@/features/Notifications';
import { usePushSubscribe } from '@/shared/hooks/usePushSubscribe';
import { getShopAvailable } from '@/shared/services/shopService';
import { Brand, useLibertyPage } from '@/shared/components/liberty';

interface NavItemDef {
  to: string;
  label: string;
  icon: React.ReactNode;
  /** Se definido, so renderiza pra GMs */
  gmOnly?: boolean;
  /** Se definido, so renderiza pra vendedores RMT */
  sellerOnly?: boolean;
  /** Se definido, so renderiza pra streamers oficiais */
  streamerOnly?: boolean;
  /** So renderiza se a loja cash do site tem categoria/produto cadastrado */
  shopOnly?: boolean;
  /** Accent roxo (Twitch) no item ativo — usado no Painel Streamer */
  accent?: 'streamer';
}

// RMT desativado em stand-by — codigo preservado, so escondemos da UI.
// Pra reativar: trocar pra true.
const RMT_ENABLED = false;

const NAV_ITEMS: NavItemDef[] = [
  { to: '/dashboard', label: 'Resumo', icon: <LayoutDashboard size={20} /> },
  { to: '/dashboard/conta', label: 'Gerenciar Conta', icon: <UserCog size={20} /> },
  { to: '/dashboard/personagens', label: 'Personagens', icon: <Users size={20} /> },
  { to: '/dashboard/recarga', label: 'Recarga', icon: <Coins size={20} /> },
  { to: '/dashboard/cashshop', label: 'lcoin Shop', icon: <Store size={20} />, shopOnly: true },
  { to: '/dashboard/premium', label: 'Premium', icon: <Crown size={20} /> },
  { to: '/dashboard/streamer', label: 'Painel Streamer', icon: <Tv size={20} />, streamerOnly: true, accent: 'streamer' },
  ...(RMT_ENABLED ? [
    { to: '/dashboard/marketplace', label: 'Marketplace', icon: <Store size={20} /> },
    { to: '/dashboard/rmt', label: 'Meu RMT', icon: <HandCoins size={20} />, sellerOnly: true },
  ] as NavItemDef[] : []),
  { to: '/dashboard/suporte', label: 'Suporte', icon: <HelpCircle size={20} /> },
  // GMs ganham um link único pro app admin separado
  { to: '/admin', label: 'Painel GM', icon: <ShieldCheck size={20} />, gmOnly: true },
];

/** Contexto que as paginas do painel recebem via useOutletContext. */
export type DashboardOutletContext = { shopOk: boolean };

const COLLAPSE_KEY = 'liberty_sidebar_collapsed';

// Tooltip do item no modo recolhido
const TIP = `absolute left-full ml-3 px-2 py-1 rounded-md text-xs font-medium
  bg-[#0b0f12] border border-white/[0.10] text-white whitespace-nowrap
  opacity-0 group-hover:opacity-100 pointer-events-none
  transition-opacity duration-200 z-50 shadow-lg`;

export default function DashboardLayout() {
  // Fundo/tokens do tema novo (liberty.css) — o layout da sidebar e' o antigo, a pedido do dono.
  useLibertyPage('panel-page');   // titulo = BRAND_NAME ("Cabal Liberty EP39")
  const { user, isLoggedIn, cash, account, logout, isSeller, isStreamer } = useAuth();
  const navigate = useNavigate();
  const isGM = account?.isGM === true;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopOk, setShopOk] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    if (!isLoggedIn) navigate('/', { replace: true });
  }, [isLoggedIn, navigate]);

  // Cash Shop so entra no menu se a loja tem conteudo cadastrado (no Liberty
  // fica vazio -> menu nem aparece).
  useEffect(() => {
    if (!isLoggedIn) return;
    getShopAvailable().then((r) => setShopOk(!!r?.available)).catch(() => setShopOk(false));
  }, [isLoggedIn]);

  // Auto-solicita permissao + inscreve em push notifications (Web Push API).
  // Idempotente: o hook tem useRef gate, roda 1x por sessao.
  usePushSubscribe({
    enabled: isLoggedIn && !!account?.userNum,
    userNum: account?.userNum ?? null,
    kind: 0,
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!isLoggedIn) return null;

  // Sidebar variant: forceExpanded ignora collapsed (uso no mobile drawer)
  const renderSidebar = (forceExpanded = false) => {
    const isCollapsed = collapsed && !forceExpanded;

    return (
      <div className="flex flex-col h-full">
        {/* Marca nova no topo (na altura do topbar); recolhida = so' o "L" */}
        <div className={`panel-side-head ${isCollapsed ? 'is-collapsed' : ''}`}>
          {isCollapsed
            ? <img src="/assets/liberty/logo.webp?v=20260926" alt="Cabal Liberty" className="panel-side-logo" onClick={() => navigate('/')} />
            : <Brand className="panel-side-brand" />}
        </div>

        {/* Navigation */}
        <nav className={`flex-1 space-y-1 overflow-y-auto overflow-x-hidden ${isCollapsed ? 'p-2' : 'py-3 px-0'}`}>
          {NAV_ITEMS.filter(item =>
            (!item.gmOnly || isGM) && (!item.sellerOnly || isSeller) && (!item.streamerOnly || isStreamer)
            && (!item.shopOnly || shopOk)
          ).map(item => {
            const isStreamerItem = item.accent === 'streamer';
            return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard'}
              onClick={() => setMobileOpen(false)}
              title={isCollapsed ? item.label : undefined}
              className={({ isActive }) => {
                const activeAccent = isStreamerItem
                  ? 'bg-[#9146FF]/12 text-[#c8a6ff]' + (isCollapsed ? ' shadow-[0_0_14px_rgba(145,70,255,0.30)]' : ' border-l-2 border-[#9146FF]')
                  : 'bg-[#4f78a8]/15 text-[#8fb9de]' + (isCollapsed ? ' shadow-[0_0_12px_rgba(79,120,168,0.28)]' : ' border-l-2 border-[#8fb9de]');
                const idleAccent = isStreamerItem
                  ? 'text-[#b58bff]/80 hover:bg-[#9146FF]/[0.08] hover:text-[#c8a6ff]'
                  : 'text-white/70 hover:bg-white/[0.05] hover:text-white';
                return `relative flex items-center rounded-none transition-colors duration-150 group
                ${isCollapsed ? 'justify-center h-11 w-11 mx-auto' : 'gap-3 px-5 py-2.5'}
                ${isActive
                  ? activeAccent
                  : idleAccent + (isCollapsed ? '' : ' border-l-2 border-transparent')
                }`;
              }}
            >
              <span className="opacity-80 group-hover:opacity-100 transition-opacity shrink-0">{item.icon}</span>
              {!isCollapsed && (
                <span className="flex-1 text-sm font-medium truncate">{item.label}</span>
              )}
              {/* Selo LIVE roxo no item do streamer (só expandido) */}
              {!isCollapsed && isStreamerItem && (
                <Radio size={13} className="shrink-0 text-[#9146FF] animate-pulse" />
              )}
              {isCollapsed && <span className={TIP}>{item.label}</span>}
            </NavLink>
            );
          })}
        </nav>

        {/* Voltar ao Site + Logout */}
        <div className={`border-t border-white/[0.06] flex flex-col gap-1 ${isCollapsed ? 'p-2' : 'p-3'}`}>
          <button
            onClick={() => navigate('/')}
            title={isCollapsed ? 'Voltar ao site' : undefined}
            className={`relative flex items-center rounded-none text-sm group
                       text-white/55 hover:text-[#8fb9de] hover:bg-[#4f78a8]/10
                       transition-colors duration-150 cursor-pointer
                       ${isCollapsed ? 'justify-center h-11 w-11 mx-auto' : 'gap-3 w-full px-4 py-2.5'}`}
          >
            <Home size={18} className="shrink-0" />
            {!isCollapsed && <span>Voltar ao site</span>}
            {isCollapsed && <span className={TIP}>Voltar ao site</span>}
          </button>

          <button
            onClick={handleLogout}
            title={isCollapsed ? 'Sair' : undefined}
            className={`relative flex items-center rounded-none text-sm group
                       text-white/55 hover:text-red-400 hover:bg-red-500/10
                       transition-colors duration-150 cursor-pointer
                       ${isCollapsed ? 'justify-center h-11 w-11 mx-auto' : 'gap-3 w-full px-4 py-2.5'}`}
          >
            <LogOut size={18} className="shrink-0" />
            {!isCollapsed && <span>Sair</span>}
            {isCollapsed && <span className={TIP}>Sair</span>}
          </button>
        </div>
      </div>
    );
  };

  const sidebarWidth = collapsed ? 'md:w-[72px]' : 'md:w-64';
  const mainPadding = collapsed ? 'md:ml-[72px]' : 'md:ml-64';

  return (
    <div className="min-h-screen flex relative text-white">
      {/* Topbar — Desktop + Mobile (chrome dark continuo com a sidebar) */}
      <header className={`fixed top-0 right-0 left-0 h-16 bg-[#080b0d]
                         border-b border-white/[0.06] flex items-center justify-between
                         px-4 md:px-8 z-50 transition-[left] duration-300
                         ${collapsed ? 'md:left-[72px]' : 'md:left-64'}`}>
        {/* Esquerda: hamburger + marca so' no mobile (no desktop a marca fica na sidebar) */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="text-white cursor-pointer md:hidden"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Direita: notif bell + saldo lcoin + saudacao + logo */}
        <div className="flex items-center gap-4">
          <NotificationBell />
          {cash && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md
                            bg-white/[0.03] border border-white/[0.08]">
              <Coins size={14} className="text-[#f0c86f]" />
              <span className="text-sm font-bold text-white tabular-nums">
                {(cash.cash ?? 0).toLocaleString('pt-BR')}
              </span>
              <span className="text-[11px] text-white/55 font-semibold">lcoin</span>
            </div>
          )}
          <div className="flex items-center gap-2.5">
            <div className="hidden sm:block text-right leading-tight">
              <p className="text-[10px] text-white/65 font-bold uppercase tracking-wider">Bem-vindo</p>
              <p className="text-sm text-white font-semibold truncate max-w-[140px]">
                {user?.username || 'Jogador'}
              </p>
            </div>
            <div className="w-9 h-9 rounded-md border border-[#4f78a8]/50 overflow-hidden">
              <img src="/assets/liberty/logo.webp?v=20260926" alt="Cabal Liberty" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar - Desktop */}
      <aside className={`hidden md:flex md:flex-col md:fixed md:inset-y-0 bg-[#060809]
                        border-r border-white/[0.06] z-40 transition-[width] duration-300 ${sidebarWidth}`}>
        {renderSidebar(false)}

        {/* Toggle collapse button — flutua na borda direita */}
        <button
          onClick={() => setCollapsed(c => !c)}
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          className="absolute top-20 -right-3 w-6 h-6 rounded-md bg-[#0b0f12]
                     border border-white/[0.10] hover:border-[#8fb9de]/50
                     hover:bg-[#11181c] hover:text-[#8fb9de]
                     text-white/70 flex items-center justify-center
                     shadow-lg transition-colors cursor-pointer z-[60]"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </aside>

      {/* Sidebar - Mobile overlay (sempre expanded no mobile) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-16 bottom-0 w-72 bg-[#060809]
                           border-r border-white/[0.06] overflow-y-auto">
            {renderSidebar(true)}
          </aside>
        </div>
      )}

      {/* Main Content — ocupa toda a largura util ao lado da sidebar */}
      <main className={`flex-1 z-10 relative pt-16 transition-[margin] duration-300 ${mainPadding}`}>
        <div className="dashboard w-full">
          <Outlet context={{ shopOk } satisfies DashboardOutletContext} />
        </div>
      </main>
    </div>
  );
}
